// SPDX-FileCopyrightText: 2016-2026 Lari Natri <lari.natri@iki.fi>
// SPDX-License-Identifier: GPL-3.0-or-later

/** Client-side Service Worker registration and lightweight cache refresh trigger. */

const REFRESH_MESSAGE = "SB_REFRESH_CACHES_IF_STALE";
const NETWORK_STATUS_MESSAGE = "SB_NETWORK_STATUS";
const MULTIKEY_REFRESH_MESSAGE = "SB_REFRESH_MULTIKEY_SOURCES";

function postRefreshMessage(registration: ServiceWorkerRegistration): void {
  const worker = registration.active ?? registration.waiting ?? registration.installing;
  worker?.postMessage({ type: REFRESH_MESSAGE });
}

/** Keeps the worker informed because Chromium's worker navigator.onLine can lag. */
function postNetworkStatus(registration: ServiceWorkerRegistration): void {
  const worker =
    navigator.serviceWorker.controller ??
    registration.active ??
    registration.waiting ??
    registration.installing;
  worker?.postMessage({ type: NETWORK_STATUS_MESSAGE, online: navigator.onLine });
}

/**
 * Registers the app Service Worker and asks it to refresh stale cache entries
 * when the app starts and when the tab becomes visible again.
 */
export function registerOfflineSupport(): void {
  if (!("serviceWorker" in navigator)) return;

  let maintenance: AbortController | undefined;
  let sourceRefresh: Promise<void> | undefined;
  navigator.serviceWorker.addEventListener("message", event => {
    if (event.data?.type !== MULTIKEY_REFRESH_MESSAGE || sourceRefresh) return;
    maintenance = new AbortController();
    const signal = maintenance.signal;
    sourceRefresh = import("./multikey-source-refresh")
      .then(module => module.refreshMultikeySources(signal))
      .catch(() => {
        // Best-effort maintenance must never interrupt the ordinary reader.
      })
      .finally(() => {
        sourceRefresh = undefined;
      });
  });
  window.addEventListener("pagehide", () => maintenance?.abort());
  window.addEventListener("offline", () => maintenance?.abort());

  void navigator.serviceWorker
    .register("/service-worker.js", { scope: "/" })
    .then(registration => {
      postRefreshMessage(registration);
      postNetworkStatus(registration);

      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") postRefreshMessage(registration);
      });

      navigator.serviceWorker.addEventListener("controllerchange", () => {
        postRefreshMessage(registration);
        postNetworkStatus(registration);
      });

      window.addEventListener("online", () => postNetworkStatus(registration));
      window.addEventListener("offline", () => postNetworkStatus(registration));
    })
    .catch(() => {
      // Offline support is best-effort; the app must continue without it.
    });
}
